import React, { CSSProperties, ReactNode, useMemo, JSX } from 'react';
import { Breakpoint, Dialog, DialogActions, DialogContent, DialogTitle, Paper } from '@mui/material';
import { AppButton, AppTypography } from '@sprytki-frontend-apps/common/Components';
import { ConditionalWrapper, CrossIcon, Form } from '@sprytki-frontend-apps/common';

export interface ModalProps {
  show: boolean;
  onClick: (data?: any) => void;
  onCancel?: () => void;
  header?: HeaderType;
  content?: ContentType;
  hasCancelButton?: boolean;
  hasMainButton?: boolean;
  mainButton?: ButtonType & { smallButton?: boolean };
  cancelButton?: ButtonType;
  showCloseButton?: boolean;
  isLoading?: boolean;
  form?: boolean;
  dontWrapButtonText?: boolean;
  maxWidth?: Breakpoint | false;
}

type HeaderType = {
  text?: string;
  icon?: JSX.Element;
  color?: string;
};

type ContentType = {
  element?: React.ReactNode;
  textalign?: CSSProperties['textAlign'];
};

type ButtonType = {
  text?: string;
  isDisabled?: boolean;
  type?: 'button' | 'submit';
};

const AppModal: React.FC<ModalProps> = ({
  show,
  onClick,
  onCancel,
  showCloseButton,
  hasCancelButton = false,
  hasMainButton = true,
  header,
  mainButton,
  cancelButton,
  content,
  form = false,
  dontWrapButtonText,
  maxWidth = 'xs',
}) => {
  const generatedHeader = useMemo(() => {
    return (
      <>
        {header?.icon && header.icon}
        {header?.text && (
          <AppTypography element="h4" text={header.text} textAlign="center" bold color={header.color} />
        )}
        {showCloseButton && (
          <button type="button" onClick={onCancel} aria-label="close">
            <CrossIcon />
          </button>
        )}
      </>
    );
  }, [header, onCancel, showCloseButton]);

  const generatedButtons = useMemo(() => {
    const mainButtonProps = {
      text: `${mainButton?.text ?? ''}`,
      width: 'small',
      fullWidth: !mainButton?.smallButton,
      ...mainButton,
      wrapText: true,
    } as any;

    return (
      <>
        {hasCancelButton && (
          <AppButton
            text={`${cancelButton?.text ?? ''}`}
            onClick={onCancel}
            variant="outlined"
            fullWidth={!mainButton?.smallButton}
            disabled={cancelButton?.isDisabled}
            wrapText
          />
        )}
        {hasMainButton && (
          <AppButton
            {...mainButtonProps}
            onClick={onClick}
            disabled={mainButton?.isDisabled}
            wrapText={!dontWrapButtonText}
          />
        )}
      </>
    );
  }, [mainButton, cancelButton, hasCancelButton, hasMainButton, dontWrapButtonText, onCancel, onClick]);

  const handleClose = (_event: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason !== 'backdropClick' && onCancel) {
      onCancel();
    }
  };

  const hasGeneratedButtons = hasCancelButton || hasMainButton;

  const contentElement = content?.element ? (
    <DialogContent
      sx={{
        padding: '20px 20px 0',
        overflow: 'unset',
        textAlign: content.textalign ?? 'center',
        fontSize: '1rem',
        whiteSpace: 'pre-line',
      }}
    >
      {content.element}
    </DialogContent>
  ) : null;

  const actionsElement = hasGeneratedButtons ? (
    <DialogActions
      sx={{
        padding: '20px',
        display: 'flex',
        flexFlow: 'row nowrap',
        justifyContent: 'center',
      }}
    >
      {generatedButtons}
    </DialogActions>
  ) : null;

  return (
    <Dialog
      maxWidth={maxWidth}
      fullWidth
      open={show}
      onClose={handleClose}
      disableEscapeKeyDown
      PaperComponent={Paper}
    >
      <DialogTitle
        sx={{
          padding: '20px',
          display: 'flex',
          flexFlow: 'column nowrap',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          width: '100%',
          position: 'relative',
        }}
      >
        {generatedHeader}
      </DialogTitle>
      <ConditionalWrapper
        condition={form}
        wrapper={(children: ReactNode) => <Form onSubmit={() => {}}>{children}</Form>}
      >
        <>
          {contentElement}
          {actionsElement}
        </>
      </ConditionalWrapper>
    </Dialog>
  );
};

export default AppModal;
